import React, { useMemo } from 'react';
import RcTooltip from 'rc-tooltip';
import { Button, CircularProgress, Popover, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import actions from '@src/redux/actions';
import { COLOR } from '@src/styles/color';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { Trans, useTranslation } from 'react-i18next';
import {
  CONVERTING_STATUS,
  REWRITE_STATUS,
  TRANSLATION_STATUS,
} from '@src/constants/project';
import { EXPORT_TYPE } from '@src/constants/presentation';
import route from '@src/constants/route';
import { checkFreePackage, checkPackageExpired } from '@src/services/package';
import useExportAudio from '@src/hooks/useExportAudio';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import { downloadFileByUrl, getFileName } from '@src/utils/file';
import {
  audioExpired,
  getDubbingBlockChanged,
  getVideoDubbingBlockChanged,
  hasEmptyTextBlock,
  isVideoSource,
} from '@src/services/project';
import ExportRetentionNote from '../ExportPanel/ExportRetentionNote';
import ExportPanel from '../ExportPanel';
import {
  PopoverButton,
  StyledDownloadMenu,
  PopoverMenuItem,
} from './index.style';

const FILE_TYPE_OPTIONS = [
  { label: 'srtFile', value: 'srtFile' },
  { label: 'audioFile', value: 'audio' },
  { label: 'videoFileType', value: EXPORT_TYPE.VIDEO },
];

// Mirrors the TTS download button's structure options (DownloadButton.jsx).
const AUDIO_STRUCTURE_OPTIONS = [
  { label: 'singleFile', value: 'singleFile' }, // merged, exported by the BE
  { label: 'paragraphsZip', value: 'paragraphsZip' }, // per-block zip, exported by the BE
];

// Each UI audio structure maps to a backend export type. The BE builds both:
// JOIN_AUDIO merges blocks on the video timeline, SEPARATE_AUDIO returns a zip
// of per-block audio ({order}_{audioName} entries).
const STRUCTURE_EXPORT_TYPE = {
  singleFile: EXPORT_TYPE.JOIN_AUDIO,
  paragraphsZip: EXPORT_TYPE.SEPARATE_AUDIO,
};

const DownloadButtonContent = ({ showLoading }) => {
  const { t } = useTranslation();
  const exportHistoryEnabled = useExportHistoryEnabled();
  if (showLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <CircularProgress
          value={75}
          className="circular-progress"
          size="16px"
        />
      </div>
    );
  }
  return t(exportHistoryEnabled ? 'exportFileAction' : 'download');
};

const PopoverDropdown = ({
  label,
  options,
  currentOption,
  onChangeOption,
  disabled,
  tParams,
}) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = React.useState(null);

  const handleClick = (e) => {
    if (disabled) return;
    setAnchor(e.currentTarget);
  };

  const handleClose = () => {
    setAnchor(null);
  };

  const renderArrow = () => {
    if (anchor) {
      return <ArrowDropUpRoundedIcon width={24} height={24} />;
    }
    return <ArrowDropDownRoundedIcon width={24} height={24} />;
  };

  return (
    <div className="row">
      <div className="label">{label}</div>
      <PopoverButton active={!!anchor}>
        <Button
          className="custom-button"
          onClick={handleClick}
          disabled={disabled}
        >
          <span className="title-button">
            {t(currentOption?.label, tParams)}
          </span>
          {renderArrow()}
        </Button>
        <Popover
          open={Boolean(anchor)}
          anchorEl={anchor}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          className="popover-structure"
          PaperProps={{
            style: { borderRadius: '8px' },
          }}
        >
          <div
            style={{
              padding: '8px 0px',
              border: '1px solid #1E164E52',
              borderRadius: '8px',
            }}
          >
            {options.map((item) => (
              <PopoverMenuItem
                onClick={() => {
                  onChangeOption(item);
                  setAnchor(null);
                }}
                active={currentOption?.value === item?.value}
                key={item.label}
              >
                <div className="wrap-label">
                  <Typography variant="body2" className="text">
                    {t(item.label, tParams)}
                  </Typography>
                  {currentOption?.value === item?.value && (
                    <CheckRoundedIcon sx={{ color: COLOR.darkBlue }} />
                  )}
                </div>
              </PopoverMenuItem>
            ))}
          </div>
        </Popover>
      </PopoverButton>
    </div>
  );
};

const OverlayComponent = ({
  handleDownload,
  currentFileType,
  onChangeFileType,
  currentStructure,
  onChangeStructure,
  isExportBlocked,
  isDropdownBlocked,
  disabledByFileType,
  audioType,
  fileTypeOptions,
  showFreeVideoLimitNote,
  onUpgrade,
}) => {
  const { t } = useTranslation();

  const fileTypeTParams = { format: audioType?.toLowerCase() };
  const isCurrentDownloadDisabled = disabledByFileType[currentFileType?.value];

  return (
    <StyledDownloadMenu>
      <PopoverDropdown
        label={t('fileTypeDownload')}
        options={fileTypeOptions}
        currentOption={currentFileType}
        onChangeOption={onChangeFileType}
        tParams={fileTypeTParams}
        disabled={isDropdownBlocked}
      />
      {currentFileType?.value === 'audio' && (
        <PopoverDropdown
          label={t('type')}
          options={AUDIO_STRUCTURE_OPTIONS}
          currentOption={currentStructure}
          onChangeOption={onChangeStructure}
          disabled={isDropdownBlocked}
        />
      )}
      {showFreeVideoLimitNote && (
        <div className="free-limit-note">
          <Trans
            i18nKey="freeVideoExportLimitNote"
            components={[
              <b />,
              <b />,
              <b />,
              <span
                className="upgrade-link"
                role="link"
                tabIndex={0}
                onClick={onUpgrade}
              />,
            ]}
          />
        </div>
      )}
      <ExportRetentionNote />
      <Button
        className="download-btn"
        variant="contained"
        onClick={() => handleDownload()}
        disabled={isExportBlocked || isCurrentDownloadDisabled}
      >
        <DownloadButtonContent showLoading={isExportBlocked} />
      </Button>
    </StyledDownloadMenu>
  );
};

const VideoDubbingDownloadButton = () => {
  const dispatch = useDispatch();
  const {
    multiBlock,
    title,
    isDownloading,
    audioType,
    originalAudioType,
    exports,
    translation,
    source,
  } = useSelector((state) => state.project);

  const user = useSelector((state) => state.auth.user);
  const { getFeatureValue } = useFeatureFlags();

  const history = useHistory();
  const location = useLocation();
  const projectId = location.pathname.split('/')[2];

  // Feature flag gates the MP4 option at the UI layer only.
  const videoExportEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_EXPORT_VIDEO,
    { userId: user?.id, email: user?.email },
  );
  const fileTypeOptions = useMemo(
    () =>
      videoExportEnabled
        ? FILE_TYPE_OPTIONS
        : FILE_TYPE_OPTIONS.filter(
            (option) => option.value !== EXPORT_TYPE.VIDEO,
          ),
    [videoExportEnabled],
  );

  const [currentFileType, setCurrentFileType] = React.useState(
    FILE_TYPE_OPTIONS[0],
  );
  const [currentStructure, setCurrentStructure] = React.useState(
    AUDIO_STRUCTURE_OPTIONS[0],
  );
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);

  const closeTooltip = () => setIsTooltipOpen(false);

  const isLimitedUser =
    checkFreePackage(user?.packageCode) ||
    checkPackageExpired(user?.packageExpiryDate);
  const handleUpgrade = () => {
    closeTooltip();
    history.push(route.PAYMENT);
  };
  const joinAudioExport = useExportAudio({
    projectId,
    exportType: EXPORT_TYPE.JOIN_AUDIO,
    onSuccess: closeTooltip,
  });
  const separateAudioExport = useExportAudio({
    projectId,
    exportType: EXPORT_TYPE.SEPARATE_AUDIO,
    onSuccess: closeTooltip,
  });
  const videoExport = useExportAudio({
    projectId,
    exportType: EXPORT_TYPE.VIDEO,
    onSuccess: closeTooltip,
  });
  const exportHistoryEnabled = useExportHistoryEnabled();
  const srtExport = useExportAudio({
    projectId,
    exportType: EXPORT_TYPE.SRT,
    onSuccess: closeTooltip,
  });

  const { blocks, originalBlocks, blockIds, originalBlockIds } =
    multiBlock || {};
  const isTranslationSuccess =
    translation?.status === TRANSLATION_STATUS.SUCCESS;
  const blockWithsText = Object.values(blocks || {}).filter((block) =>
    isTranslationSuccess ? block?.translatedText : block?.text,
  );

  // Disable the audio download button: ignore endTime (it auto-shrinks after
  // synthesis, so it is not a real user edit).
  const dubbingBlocksChanged = useMemo(
    () =>
      getVideoDubbingBlockChanged(
        blockWithsText,
        originalBlocks || {},
        isTranslationSuccess,
      ),
    [blockWithsText, originalBlocks, isTranslationSuccess],
  );
  // Reuse the cached merged audio: endTime MUST count — a different endTime
  // yields a different merged file, so the export has to be regenerated.
  const dubbingBlocksChangedForReuse = useMemo(
    () =>
      getDubbingBlockChanged(
        blockWithsText,
        originalBlocks || {},
        isTranslationSuccess,
      ),
    [blockWithsText, originalBlocks, isTranslationSuccess],
  );

  const isRewriting = Object.values(blocks || {}).some(
    (block) => block?.rewrite?.status === REWRITE_STATUS.PENDING,
  );

  const isSynthesisInProgress = blockWithsText.some(
    (block) => block.status === CONVERTING_STATUS.IN_PROGRESS,
  );

  const hasAudioExpired = blockWithsText.some((block) =>
    audioExpired(block?.audioExpiredAt),
  );

  // A text block that has never been synthesized (no audioLink) or didn't
  // finish successfully has no audio to export. This is NOT caught by
  // dubbingBlocksChanged (an untouched-but-unsynthesized block isn't "changed")
  // nor by hasAudioExpired (it has no audioExpiredAt), so check it explicitly.
  const hasBlockMissingAudio = blockWithsText.some(
    (block) => !block?.audioLink || block?.status !== CONVERTING_STATUS.SUCCESS,
  );

  // A video project renders every block onto the video timeline, so a block
  // with no character has nothing to synthesize and would be silently dropped
  // from the merged audio / mp4. Block it at the download gate instead. SRT is
  // unaffected: it exports the blocks that do have text. Scoped to a video
  // source so SRT/audio dubbing projects keep their current behavior.
  const hasEmptyBlock = useMemo(
    () =>
      isVideoSource(source) &&
      hasEmptyTextBlock(blocks, { useTranslatedText: isTranslationSuccess }),
    [source, blocks, isTranslationSuccess],
  );

  const isDisabledSrt = blockWithsText.length === 0;

  // Audio (both merged export and per-block zip) needs synthesized, unexpired
  // audio for the current, unchanged block set.
  const isDisabledAudio = useMemo(() => {
    if (!blockWithsText.length) return true;
    if (hasEmptyBlock) return true;
    if (hasAudioExpired) return true;
    if (isSynthesisInProgress) return true;
    if (dubbingBlocksChanged?.length > 0) return true;
    if (hasBlockMissingAudio) return true;
    return false;
  }, [
    blockWithsText,
    hasEmptyBlock,
    hasAudioExpired,
    isSynthesisInProgress,
    dubbingBlocksChanged,
    hasBlockMissingAudio,
  ]);

  const disabledByFileType = {
    srtFile: isDisabledSrt,
    audio: isDisabledAudio,
    // Video needs the same synthesized, unexpired, unchanged voiceover audio.
    [EXPORT_TYPE.VIDEO]: isDisabledAudio,
  };

  const isDisabledOuterButton = isDisabledSrt && isDisabledAudio;

  const exportJoinAudio = exports?.[EXPORT_TYPE.JOIN_AUDIO];
  const exportSeparateAudio = exports?.[EXPORT_TYPE.SEPARATE_AUDIO];

  // A cached export can be reused only when nothing that changes its output has
  // moved since it was exported: block content/order + audio type + no changed
  // blocks. The changed-block list differs per type (see below).
  // With the flag on every click is a new export, so a cached file is never
  // reused; with it off the previous behaviour stands.
  const canReuseExport = (exportEntry, changedBlocks) =>
    !exportHistoryEnabled &&
    !!exportEntry?.fileUrl &&
    changedBlocks?.length === 0 &&
    blockIds?.length === originalBlockIds?.length &&
    originalAudioType === audioType;

  // JOIN_AUDIO is timeline-aligned, so endTime counts (dubbingBlocksChangedForReuse):
  // a different endTime yields a different merged file and must be regenerated.
  const canReuseJoinAudio = canReuseExport(
    exportJoinAudio,
    dubbingBlocksChangedForReuse,
  );
  // SEPARATE_AUDIO is a zip of raw per-block audio and the BE payload carries no
  // timing, so endTime is ignored (dubbingBlocksChanged) — an endTime-only edit
  // does not change the zip content. Consistent with the isDisabledAudio gate.
  const canReuseSeparateAudio = canReuseExport(
    exportSeparateAudio,
    dubbingBlocksChanged,
  );

  const exportVideo = exports?.[EXPORT_TYPE.VIDEO];
  // Video is timeline-aligned (like JOIN_AUDIO), so endTime counts for reuse:
  // a different endTime yields a different render and must be regenerated.
  const canReuseVideo = canReuseExport(
    exportVideo,
    dubbingBlocksChangedForReuse,
  );

  const isLoading =
    isDownloading ||
    joinAudioExport.isExporting ||
    separateAudioExport.isExporting ||
    videoExport.isExporting ||
    srtExport.isExporting;

  const isExportingByType = {
    [EXPORT_TYPE.JOIN_AUDIO]: joinAudioExport.isExporting,
    [EXPORT_TYPE.SEPARATE_AUDIO]: separateAudioExport.isExporting,
    [EXPORT_TYPE.VIDEO]: videoExport.isExporting,
    [EXPORT_TYPE.SRT]: srtExport.isExporting,
  };

  // The type the popup would export right now.
  const currentExportType =
    // eslint-disable-next-line no-nested-ternary
    currentFileType?.value === 'audio'
      ? STRUCTURE_EXPORT_TYPE[currentStructure?.value]
      : currentFileType?.value === 'srtFile'
      ? EXPORT_TYPE.SRT
      : currentFileType?.value;

  // Flag on: one export per type at a time, so only the type already running is
  // blocked and the type dropdown stays usable. Flag off: any export in flight
  // blocks the whole popup, as before.
  const isExportBlocked = exportHistoryEnabled
    ? isDownloading || Boolean(isExportingByType[currentExportType])
    : isLoading;
  const isDropdownBlocked = exportHistoryEnabled ? isDownloading : isLoading;

  const handleDownloadSrtFile = async () => {
    // The backend builds the file now, so the subtitle download is an export
    // like any other: it is tracked, named by BR_5 and kept in the history.
    if (exportHistoryEnabled) {
      srtExport.startExport();
      return;
    }

    try {
      dispatch(actions.project.setIsDownloading(true));
      const blockToSRT = (block, index) => {
        const content =
          (isTranslationSuccess ? block?.translatedText : block?.text) || '';
        return `${index}\n${block.startTime} --> ${block.endTime}\n${content}\n\n`;
      };

      const srtContent = blockWithsText
        .map((block, index) => blockToSRT(block, index + 1))
        .join('');

      const blob = new Blob([srtContent], {
        type: 'text/plain;charset=utf-8;',
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title}.srt`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      dispatch(actions.project.setIsDownloading(false));
      setIsTooltipOpen(false);
    }
  };

  const handleDownloadAudio = (exportType) => {
    const isJoinAudio = exportType === EXPORT_TYPE.JOIN_AUDIO;
    const exportEntry = exports?.[exportType];
    const canReuse = isJoinAudio ? canReuseJoinAudio : canReuseSeparateAudio;
    // Reuse the already-exported file when nothing changed; otherwise export.
    if (canReuse) {
      setIsTooltipOpen(false);
      downloadFileByUrl(exportEntry.fileUrl, getFileName(exportEntry.fileUrl));
      return;
    }
    const { startExport } = isJoinAudio ? joinAudioExport : separateAudioExport;
    startExport();
  };

  const handleDownloadVideo = () => {
    // Reuse the already-rendered mp4 when nothing changed; otherwise export.
    if (canReuseVideo) {
      setIsTooltipOpen(false);
      downloadFileByUrl(exportVideo.fileUrl, getFileName(exportVideo.fileUrl));
      return;
    }
    videoExport.startExport();
  };

  const handleDownload = () => {
    if (currentFileType?.value === 'srtFile') {
      handleDownloadSrtFile();
      return;
    }
    if (currentFileType?.value === EXPORT_TYPE.VIDEO) {
      handleDownloadVideo();
      return;
    }
    handleDownloadAudio(STRUCTURE_EXPORT_TYPE[currentStructure?.value]);
  };

  const showLoadingButton = isLoading && !isTooltipOpen;

  return (
    <RcTooltip
      placement="bottomRight"
      overlay={
        <ExportPanel isOpen={isTooltipOpen}>
          <OverlayComponent
            currentFileType={currentFileType}
            onChangeFileType={setCurrentFileType}
            currentStructure={currentStructure}
            onChangeStructure={setCurrentStructure}
            handleDownload={handleDownload}
            isExportBlocked={isExportBlocked}
            isDropdownBlocked={isDropdownBlocked}
            disabledByFileType={disabledByFileType}
            audioType={audioType}
            fileTypeOptions={fileTypeOptions}
            showFreeVideoLimitNote={
              isLimitedUser && currentFileType?.value === EXPORT_TYPE.VIDEO
            }
            onUpgrade={handleUpgrade}
          />
        </ExportPanel>
      }
      trigger={['click']}
      showArrow={false}
      align={{
        offset: [0, 5],
      }}
      onVisibleChange={setIsTooltipOpen}
      visible={isTooltipOpen}
      destroyTooltipOnHide
    >
      <Button
        startIcon={
          !showLoadingButton && (
            <DownloadRoundedIcon style={{ fontSize: '24px' }} />
          )
        }
        className="download-btn"
        variant="contained"
        disabled={
          (isDisabledOuterButton || isRewriting) && !exportHistoryEnabled
        }
        onClick={() => setIsTooltipOpen(!isTooltipOpen)}
      >
        <DownloadButtonContent showLoading={showLoadingButton} />
      </Button>
    </RcTooltip>
  );
};

export default VideoDubbingDownloadButton;
